import { Head, useForm, router } from "@inertiajs/react"
import { useState } from "react"
import { track } from "@/lib/analytics"
import type { Membership, Invitation } from "@/types"
import DashboardLayout from "@/components/dashboard/layout"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"

type MembershipWithUser = Membership & {
  user: { id: number; name: string; email: string; avatar_url?: string | null }
}

export default function MembersIndex({
  memberships,
  invitations,
  current_role,
}: {
  memberships: MembershipWithUser[]
  invitations: Invitation[]
  current_role: string
}) {
  const [showInvite, setShowInvite] = useState(false)
  const canManage = current_role === "owner" || current_role === "admin"

  return (
    <DashboardLayout>
      <Head title="Team | QuiteRight" />

      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight text-charcoal">
            Team
          </h1>
          <p className="mt-1 text-warm-grey">
            Manage your team members and invitations.
          </p>
        </div>
        {canManage && (
          <Button
            onClick={() => setShowInvite(true)}
            className="bg-red text-white hover:bg-red-deep"
          >
            Invite member
          </Button>
        )}
      </div>

      {/* Invite dialog */}
      {showInvite && (
        <InviteForm onClose={() => setShowInvite(false)} />
      )}

      {/* Members table */}
      <div className="rounded-xl border border-stone/40 bg-white">
        <div className="border-b border-stone/40 px-6 py-4">
          <h2 className="font-display text-lg font-semibold text-charcoal">
            Members ({memberships.length})
          </h2>
        </div>
        <div className="divide-y divide-stone/30">
          {memberships.map((m) => (
            <div key={m.id} className="flex items-center justify-between px-6 py-4">
              <div className="flex items-center gap-3">
                <Avatar className="size-9">
                  <AvatarImage src={m.user.avatar_url || undefined} />
                  <AvatarFallback className="bg-stone/40 text-xs">
                    {m.user.name
                      .split(" ")
                      .map((n) => n[0])
                      .join("")
                      .toUpperCase()
                      .slice(0, 2)}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="text-sm font-medium text-charcoal">{m.user.name}</p>
                  <p className="text-xs text-warm-grey">{m.user.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Badge variant="outline" className="capitalize">{m.role}</Badge>
                {canManage && m.role !== "owner" && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="size-8 p-0">
                        <svg className="size-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 12.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 18.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5Z" />
                        </svg>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {current_role === "owner" && (
                        <>
                          <DropdownMenuItem
                            onClick={() => {
                              track("member_role_changed", { role: "admin" })
                              router.patch(`/dashboard/members/${m.public_id}`, { role: "admin" })
                            }}
                          >
                            Make admin
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => {
                              track("member_role_changed", { role: "member" })
                              router.patch(`/dashboard/members/${m.public_id}`, { role: "member" })
                            }}
                          >
                            Make member
                          </DropdownMenuItem>
                        </>
                      )}
                      <DropdownMenuItem
                        onClick={() => {
                          if (confirm("Remove this member from the team?")) {
                            track("member_removed")
                            router.delete(`/dashboard/members/${m.public_id}`)
                          }
                        }}
                        className="text-red"
                      >
                        Remove
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Pending invitations */}
      {invitations.length > 0 && (
        <div className="mt-8 rounded-xl border border-stone/40 bg-white">
          <div className="border-b border-stone/40 px-6 py-4">
            <h2 className="font-display text-lg font-semibold text-charcoal">
              Pending invitations ({invitations.length})
            </h2>
          </div>
          <div className="divide-y divide-stone/30">
            {invitations.map((inv) => (
              <div key={inv.id} className="flex items-center justify-between px-6 py-4">
                <div>
                  <p className="text-sm font-medium text-charcoal">{inv.email}</p>
                  <p className="text-xs text-warm-grey">
                    Invited as {inv.role} &middot; expires{" "}
                    {new Date(inv.expires_at).toLocaleDateString("en-GB")}
                  </p>
                </div>
                {canManage && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      track("invitation_revoked")
                      router.delete(`/dashboard/invitations/${inv.public_id}`)
                    }}
                    className="text-warm-grey hover:text-red"
                  >
                    Revoke
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </DashboardLayout>
  )
}

function InviteForm({ onClose }: { onClose: () => void }) {
  const { data, setData, post, processing, errors, reset, transform } = useForm({
    email: "",
    role: "member",
  })

  transform((d) => ({ invitation: d }))

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    post("/dashboard/invitations", {
      onSuccess: () => {
        track("member_invited", { role: data.role })
        reset()
        onClose()
      },
    })
  }

  return (
    <div className="mb-8 rounded-xl border border-red/20 bg-white p-6">
      <h3 className="mb-4 font-display text-lg font-semibold text-charcoal">
        Invite a team member
      </h3>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4 sm:flex-row sm:items-end">
        <div className="flex-1">
          <Label htmlFor="invite_email">Email address</Label>
          <Input
            id="invite_email"
            type="email"
            value={data.email}
            onChange={(e) => setData("email", e.target.value)}
            placeholder="colleague@agency.co.uk"
            className="mt-1.5"
            required
          />
          {errors.email && <p className="mt-1 text-xs text-red">{errors.email}</p>}
        </div>
        <div className="w-36">
          <Label htmlFor="invite_role">Role</Label>
          <select
            id="invite_role"
            value={data.role}
            onChange={(e) => setData("role", e.target.value)}
            className="mt-1.5 w-full rounded-md border border-stone/40 bg-white px-3 py-2 text-sm"
          >
            <option value="member">Member</option>
            <option value="admin">Admin</option>
          </select>
        </div>
        <div className="flex gap-2">
          <Button type="submit" disabled={processing} className="bg-red text-white hover:bg-red-deep">
            {processing ? "Sending..." : "Send invite"}
          </Button>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </form>
    </div>
  )
}
